import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';

import { init } from 'contentful-ui-extensions-sdk';
import './index.css';
import './App.scss';
import { Button, Stack, Card, Text, Notification } from '@contentful/f36-components';
import { PlusIcon } from '@contentful/f36-icons';
import { AttributeSelector } from './components/AttributeSelector';
import type { Attribute, ContentfulAttribute, LegacyContentfulAttribute } from './types/types';
import { getNewAttribute, migrateLegacyAttribute } from './factory/AttributeFactory';

export const App = ({ sdk }) => {
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [selectedAttributes, setSelectedAttributes] = useState<ContentfulAttribute[]>([]);
  const [haveThereBeenChanges, setHaveThereBeenChanges] = useState<boolean>(false);
  const [storedPreFilter, setStoredPreFilter] = useState<ContentfulAttribute[] | null>(null);

  const parseAttributes = (rawAttributes: Record<string, string[]>): Attribute[] => {
    const typedAttributes: Attribute[] = [];
    for (const name in rawAttributes) {
      typedAttributes.push({ name, values: rawAttributes[name] });
    }
    return typedAttributes;
  };

  const isLegacyAttribute = (object: unknown): object is LegacyContentfulAttribute => {
    return (
      Object.prototype.hasOwnProperty.call(object, 'attribute') &&
      Object.prototype.hasOwnProperty.call(object, 'attributeValue')
    );
  };

  const hasLegacyPreFilter = (storedPreFilter: unknown[]): boolean => {
    return storedPreFilter && storedPreFilter.length > 0 && isLegacyAttribute(storedPreFilter[0]);
  };

  const isFirstGenerationPreFilter = (storedPreFilter: unknown) => {
    return storedPreFilter && !Array.isArray(storedPreFilter);
  };

  const loadPreFilter = (): ContentfulAttribute[] => {
    const localStoredPreFilter = sdk.field.getValue();
    setStoredPreFilter(localStoredPreFilter);

    if (isFirstGenerationPreFilter(localStoredPreFilter)) {
      const newPrefilter: ContentfulAttribute[] = [];

      sdk.field.setValue(newPrefilter).then(() => console.log('first generation pre-filter removed.'));
      sdk.notifier.error(
        `First generation pre-filter removed. You will have to manually set up the pre-filter again before publishing.`
      );

      return newPrefilter;
    } else if (hasLegacyPreFilter(localStoredPreFilter)) {
      const newPrefilter: ContentfulAttribute[] = [];

      for (const legacyAttribute of localStoredPreFilter) {
        if (isLegacyAttribute(legacyAttribute)) {
          newPrefilter.push(migrateLegacyAttribute(legacyAttribute));
        }
      }

      sdk.field.setValue(newPrefilter).then(() => console.log('legacy attribute updated to new format.'));

      return newPrefilter;
    }
    return localStoredPreFilter;
  };

  useEffect(() => {
    sdk.window.startAutoResizer();

    const fetchAttributes = async () => {
      try {
        const response = await fetch(process.env.AGGREGATED_ATTRIBUTES_URL ?? '');
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const rawAttributes = await response.json();
        const typedAttributes: Attribute[] = parseAttributes(rawAttributes);
        setAttributes(typedAttributes);

        const storedPreFilter = loadPreFilter();
        if (storedPreFilter) {
          setSelectedAttributes(storedPreFilter);
        }
      } catch (error) {
        console.log('Error retrieving attributes', error);
      }
    };

    fetchAttributes();
  }, []);

  useEffect(() => {
    setHaveThereBeenChanges(JSON.stringify(selectedAttributes) !== JSON.stringify(storedPreFilter));
  }, [selectedAttributes, storedPreFilter]);

  const onAddAttribute = () => {
    setSelectedAttributes([...selectedAttributes, getNewAttribute()]);
  };

  const onRemoveAttribute = (id: string) => {
    const attributeIndex = selectedAttributes.findIndex((a) => a.id === id);
    if (attributeIndex != -1) {
      const updatedSelectedAttributes = [...selectedAttributes];
      updatedSelectedAttributes.splice(attributeIndex, 1);
      setSelectedAttributes(updatedSelectedAttributes);
    }
  };

  const onSelectedAttributeChange = (attribute: Attribute, id: string) => {
    const attributeIndex = selectedAttributes.findIndex((a) => a.id === id);
    if (attributeIndex != -1) {
      const updatedSelectedAttributes = [...selectedAttributes];
      updatedSelectedAttributes.splice(attributeIndex, 1, { id, ...attribute });
      setSelectedAttributes(updatedSelectedAttributes);
    }
  };

  const validatePrefilter = (selectedAttributes: ContentfulAttribute[]) => {
    const validationResults: string[] = [];

    for (const attribute of selectedAttributes) {
      if (!attribute.values || attribute.values.length === 0) {
        validationResults.push(attribute.name || 'Unselected attribute');
      }
    }

    return validationResults.join(', ');
  };

  const onSavePrefilter = async () => {
    const validationResult = validatePrefilter(selectedAttributes);

    if (validationResult !== '') {
      Notification.error(validationResult, { title: 'The following attributes are missing values' });
    } else {
      setStoredPreFilter(selectedAttributes);
      await sdk.field.setValue(selectedAttributes);
    }
  };

  return (
    <div className="pre-filter-extension" style={{ width: '100%', maxWidth: '669px' }}>
      <Stack flexDirection="column" style={{ width: '100%', margin: '0 auto' }} justifyContent="space-between">
        {selectedAttributes.map((attribute) => (
          <AttributeSelector
            key={attribute.id}
            attributes={attributes}
            onRemoveAttribute={onRemoveAttribute}
            id={attribute.id}
            onSelectedAttributeChange={onSelectedAttributeChange}
            initialAttribute={attribute}
          />
        ))}
        <Card
          style={{
            borderStyle: 'dashed',
            borderColor: 'rgb(103, 114, 138)',
            margin: '0 auto'
          }}>
          <Button onClick={onAddAttribute} startIcon={<PlusIcon />} style={{ margin: '0 auto', display: 'flex' }}>
            <Text fontWeight="fontWeightDemiBold">Add attribute</Text>
          </Button>
        </Card>
        <Stack style={{ width: '100%' }} justifyContent="space-between">
          <Button onClick={onSavePrefilter} variant="primary" isDisabled={!haveThereBeenChanges}>
            Save Pre-filter
          </Button>
        </Stack>
      </Stack>
    </div>
  );
};

App.propTypes = {
  sdk: PropTypes.object.isRequired
};

init((sdk) => {
  ReactDOM.render(<App sdk={sdk} />, document.getElementById('root'));
});
